import logger from '@app/utils/logger';
import { isMappedProductionHost, normalizeLifecycleEnv, resolveRegionFromHost } from './telemetry';

const w = window as any;

const PENDO_AGENT_BASE_URL = 'https://cdn.pendo.io/agent/static';

/**
 * Queued on the stub before the real agent arrives, mirroring Pendo's own install snippet.
 * `initialize` must be first so the agent replays it before anything else.
 */
const QUEUED_AGENT_METHODS = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];

type PendoVisitor = {
  id: string;
};

type PendoAccount = {
  id: string;
  environment: string;
  region?: string;
};

const getEnvVar = (key: string): string => process.env[key] || (w._env_ && w._env_[key]) || '';

const getApiKey = (): string => getEnvVar('REACT_APP_PENDO_API_KEY');

let agentInstalled = false;
let initialized = false;

/**
 * The configured key is the only gate, so local development reports into the configured Pendo
 * subscription as well. That is deliberate — it is what makes the flow testable without a deploy —
 * and it is why an unconfigured key must stay the off switch.
 */
export const shouldInitializePendo = (): boolean => !!getApiKey();

/**
 * Non-personal account context, using the same environment and region vocabulary as Datadog RUM
 * so Pendo segments line up with existing telemetry segments.
 */
const getAccountMetadata = (accountId: string): PendoAccount => {
  const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
  const configuredEnv = getEnvVar('REACT_APP_KUBE_NAMESPACE') || getEnvVar('REACT_APP_BACKEND_ENV') || 'dev';
  const region = resolveRegionFromHost(hostname);

  const account: PendoAccount = {
    id: accountId,
    environment: isMappedProductionHost(hostname) ? 'prod' : normalizeLifecycleEnv(configuredEnv),
  };
  if (region) account.region = region;

  return account;
};

const installAgent = (apiKey: string): void => {
  if (agentInstalled) return;

  // An agent that already exposes initialize needs no stub and no second script element.
  if (typeof w.pendo?.initialize === 'function') {
    agentInstalled = true;
    return;
  }

  const pendo = (w.pendo = w.pendo || {});
  pendo._q = pendo._q || [];
  QUEUED_AGENT_METHODS.forEach((method, index) => {
    pendo[method] =
      pendo[method] ||
      function queueAgentCall(this: unknown) {
        const call = [method].concat(Array.prototype.slice.call(arguments));
        if (index === 0) pendo._q.unshift(call);
        else pendo._q.push(call);
      };
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `${PENDO_AGENT_BASE_URL}/${apiKey}/pendo.js`;
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) firstScript.parentNode.insertBefore(script, firstScript);
  else document.head.appendChild(script);

  agentInstalled = true;
};

/**
 * Identifies the signed-in visitor once Okta and tenant selection have both resolved.
 *
 * `visitorId` is the Okta `sub` claim, never the email or display name: the payload is a closed,
 * non-personal field set (see specs/58909-pendo-native-install/contracts). Initialize runs once per
 * session; later changes such as a tenant switch go through identify, because the agent rejects a
 * second initialize.
 */
export const identifyPendoVisitor = (visitorId: string, accountId: string): void => {
  if (!shouldInitializePendo() || !visitorId || !accountId) return;

  try {
    installAgent(getApiKey());

    const pendo = w.pendo;
    if (!pendo) return;

    const visitor: PendoVisitor = { id: visitorId };
    const account = getAccountMetadata(accountId);

    if (initialized) {
      pendo.identify({ visitor, account });
      return;
    }

    pendo.initialize({ visitor, account });
    initialized = true;
  } catch (error) {
    logger.warn('Pendo', 'identifyPendoVisitor', error instanceof Error ? error.message : String(error));
  }
};

/**
 * Clears Pendo identity on sign-out so a shared workstation does not attribute the next person's
 * activity to the previous visitor. The agent script stays loaded; only the identity resets, which
 * is why the next sign-in initializes again rather than identifying into a dead session.
 */
export const clearPendoSession = (): void => {
  try {
    if (typeof w.pendo?.clearSession === 'function') w.pendo.clearSession();
  } catch (error) {
    logger.warn('Pendo', 'clearPendoSession', error instanceof Error ? error.message : String(error));
  } finally {
    initialized = false;
  }
};
